import { useMemo, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Temporal } from 'temporal-polyfill';
import { computeStats } from '../domain/stats';
import type { StatWindowTotals, Stats } from '../domain/stats';
import { formatHoursMinutes } from '../domain/format';
import type { ClassSeries } from '../domain/types';
import { useAsync, useRepository } from './repositoryContext';
import { EmptyState, ErrorBanner } from './components';

type WindowId = keyof Stats;

/** Card order mirrors time itself: recent past, the present, then the future. */
const WINDOW_ORDER: readonly WindowId[] = [
  'past7Days',
  'past2Weeks',
  'pastMonth',
  'thisWeek',
  'thisMonth',
  'nextMonth',
  'next3Months',
  'untilYearEnd',
];

const WINDOW_LABELS: Record<WindowId, string> = {
  total: 'Total taught',
  past7Days: 'Past 7 days',
  past2Weeks: 'Past 2 weeks',
  pastMonth: 'Past month',
  thisWeek: 'This week',
  thisMonth: 'This month',
  nextMonth: 'Next month',
  next3Months: 'Next 3 months',
  untilYearEnd: 'Until end of year',
};

export function StatsView(props: {
  /** Injected by tests; the app uses the real clock. */
  now?: Temporal.ZonedDateTime;
}): JSX.Element {
  const repo = useRepository();
  const data = useAsync(async () => {
    const [locations, series, overrides] = await Promise.all([
      repo.listLocations(),
      repo.listSeries(),
      repo.listOverrides(),
    ]);
    return { locations, series, overrides };
  }, []);

  const [selected, setSelected] = useState<WindowId>('total');

  // `now` is frozen per data load rather than per render: stats shifting under
  // the user mid-scroll would be more confusing than being a minute stale.
  const stats = useMemo(() => {
    if (data.data === undefined) {
      return undefined;
    }
    return computeStats({ ...data.data, now: props.now ?? Temporal.Now.zonedDateTimeISO() });
  }, [data.data, props.now]);

  if (data.error !== undefined) {
    return (
      <section>
        <header class="screen-head">
          <h2>Stats</h2>
        </header>
        <ErrorBanner error={data.error} />
      </section>
    );
  }

  if (stats === undefined || data.data === undefined) {
    return (
      <section>
        <header class="screen-head">
          <h2>Stats</h2>
        </header>
        <p class="muted">Loading…</p>
      </section>
    );
  }

  if (data.data.series.length === 0) {
    return (
      <section>
        <header class="screen-head">
          <h2>Stats</h2>
        </header>
        <EmptyState title="Nothing to count yet.">
          <p>Once you add classes, your teaching hours will show up here.</p>
        </EmptyState>
      </section>
    );
  }

  const bySeriesId = new Map(data.data.series.map((item) => [item.id, item]));
  // Narrowing does not survive into the nested function, so pin it here.
  const ready = stats;

  /** The last row of each time band; a hairline below it separates past, present, future. */
  const bandEnds: readonly WindowId[] = ['pastMonth', 'thisMonth'];

  function row(id: WindowId): JSX.Element {
    const totals = ready[id];
    return (
      <li key={id} class={bandEnds.includes(id) ? 'band-end' : undefined}>
        <button
          type="button"
          class={`stat-row${selected === id ? ' selected' : ''}`}
          aria-pressed={selected === id}
          onClick={() => setSelected(id)}
        >
          <span class="stat-row-label">{WINDOW_LABELS[id]}</span>
          <span class="stat-row-count">
            {totals.sessions}
            {/* The column header is hidden from the accessible name, so each row
                carries its own unit for screen readers. */}
            <span class="visually-hidden">{totals.sessions === 1 ? ' class' : ' classes'}</span>
          </span>
          <span class="stat-row-hours">{formatHoursMinutes(totals.minutes)}</span>
        </button>
      </li>
    );
  }

  return (
    <section>
      <header class="screen-head">
        <h2>Stats</h2>
      </header>

      {/* All-time hours first: the number the whole screen exists for. */}
      <button
        type="button"
        class={`stat-card hero${selected === 'total' ? ' selected' : ''}`}
        aria-pressed={selected === 'total'}
        onClick={() => setSelected('total')}
      >
        <span class="stat-label">{WINDOW_LABELS.total}</span>
        <span class="stat-value">{formatHoursMinutes(ready.total.minutes)}</span>
        <span class="stat-note">
          {`${ready.total.sessions} ${ready.total.sessions === 1 ? 'class' : 'classes'}`}
        </span>
      </button>

      {/* One row per period, ordered as a timeline; aligned columns are the
          point — comparing periods is what this screen is for. */}
      <div class="stat-table">
        <div class="stat-table-head" aria-hidden="true">
          <span>Period</span>
          <span>Classes</span>
          <span>Hours</span>
        </div>
        <ul class="stat-rows">{WINDOW_ORDER.map((id) => row(id))}</ul>
      </div>

      <div class="group">
        <h3 class="group-heading">By class · {WINDOW_LABELS[selected]}</h3>
        <Breakdown totals={stats[selected]} bySeriesId={bySeriesId} />
      </div>
    </section>
  );
}

function Breakdown(props: {
  totals: StatWindowTotals;
  bySeriesId: ReadonlyMap<string, ClassSeries>;
}): JSX.Element {
  const totals = props.totals;
  if (totals.sessions === 0) {
    return <p class="muted">No classes in this period.</p>;
  }

  return (
    <ul class="break-list">
      {totals.bySeries.map((row) => {
        const series = props.bySeriesId.get(row.seriesId);
        const share = (row.minutes / totals.minutes) * 100;
        return (
          <li key={row.seriesId} class="break-row">
            <span class="break-title">
              <span class="row-title">
                {series?.title ?? 'Removed class'}
                {series !== undefined && !series.active ? <span class="tag">Archived</span> : null}
              </span>
              <span class="break-figures">
                {`${row.sessions} × · ${formatHoursMinutes(row.minutes)}`}
              </span>
            </span>
            <span class="break-track" aria-hidden="true">
              <span
                class="break-fill"
                style={{ width: `${share}%`, background: series?.color ?? 'var(--muted)' }}
              />
            </span>
          </li>
        );
      })}
    </ul>
  );
}
